import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SyncIcon from '@mui/icons-material/Sync';
import { lookupService } from '@/services/lookupService';

export function VersionInfoPopover({
  anchorEl,
  onClose,
  onReset,
  showReset = true,
}: Readonly<{
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onReset?: () => void;
  showReset?: boolean;
}>) {
  const { data: version, isLoading } = useQuery({
    queryKey: ['version'],
    queryFn: lookupService.getVersion,
    enabled: !!anchorEl,
    staleTime: Infinity,
    retry: false,
  });

  const rows: { label: string; value: string | undefined }[] = [
    { label: 'API Version', value: version?.apiVersion },
    { label: 'DB Version',  value: version?.dbVersion },
    { label: 'DB Name',     value: version?.dbName },
  ];

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          elevation: 4,
          sx: { borderRadius: 2, minWidth: 240, mt: 0.75 },
        },
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          System Info
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {rows.map(({ label, value }) => (
              <Stack key={label} direction="row" spacing={2} sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', textAlign: 'right', wordBreak: 'break-all' }}>
                  {value ?? '—'}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}

        {showReset && onReset && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Tooltip title="Clears local cache and reloads the app" placement="right">
              <Button
                onClick={onReset}
                size="small"
                color="warning"
                startIcon={<SyncIcon />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Reset &amp; Reload
              </Button>
            </Tooltip>
          </>
        )}
      </Box>
    </Popover>
  );
}
